import $ from 'jquery';
import { isTouchDevice } from './common';

/**
 * Returns the document object of the given DOM node (e.g. frame).
 * Note that this may throw an error, if the a frame's content comes from a different domain or, e.g., about:blank.
 * Those errors must be handled (e.g., caught) by caller.
 */
export function getDocument(frame) {
    return frame.document || frame.contentDocument || frame.contentWindow.document;
}

/**
 * Tells that a frame is accessible (and writeable) or not - for example because of cross domain restrictions.
 */
export function isAccessible(frame) {
    var isOk = false,
        varName = 'uwrFrameVar';
    try {
        // If a frame breaks the 'same origin policy' this line will result in a permission denied error.
        $(frame);
        frame.location;
        getDocument(frame);

        // Check if the frame is writable.
        frame[varName] = 13;
        isOk = frame[varName] === 13;
        frame[varName] = undefined;
    } catch (e) {
        isOk = false;
    }
    return isOk;
}

export function isTopAccessibleAncestor(win) {
    return win === win.parent || !isAccessible(win.parent);
}

function isUwrLoadedIn(frame) {
    return !!(frame.uwr && frame.uwr.version);
}

/**
 * Get the parent frame of the currentFrame, if it is accessible by the script.
 */
function getAccessibleParentFrame(currentFrame) {
    var targetFrame;
    if (isAccessible(currentFrame.parent)) {
        targetFrame = currentFrame.parent;
    }
    return targetFrame;
}

/**
 * Get the array of the accessible frames in a top-down order. UWR loaded is not checked.
 */
function getAccessibleFramesInHierarchy(frame) {
    var lastFrame = frame,
        ancestorFrames = [];
    ancestorFrames.push(lastFrame);

    while (window.top !== lastFrame && lastFrame !== undefined) {
        lastFrame = getAccessibleParentFrame(lastFrame);
        if (lastFrame !== undefined) {
            ancestorFrames.push(lastFrame);
        }
    }

    ancestorFrames.reverse();
    return ancestorFrames;
}

/**
 * Returns the top or the topmost accessible frame starting from the actual window frame. Accessible means writable
 * and the additional conditions based on the given configuration. The method stops at the first inaccessible frame.
 * @param {object} config An object which contains parameters to set up additional conditions for the
 *                 availability check.
 *                 Current parameters:
 *                 - uwrLoaded: the frame contains a loaded uwr library or not
 * @return {object} The topmost available HTML frame starting from the actual window frame.
 */
export function getTargetFrameForOverlay(config) {
    var targetFrame,
        availableFrames;
    if (!config) {
        config = {};
    }
    if (isAccessible(window.top) && (config.uwrLoaded !== true || isUwrLoadedIn(window.top))) {
        targetFrame = window.top;
    } else {
        availableFrames = getAccessibleFramesInHierarchy(window);
        targetFrame = availableFrames.shift();
        if (config.uwrLoaded === true) {
            while (!isUwrLoadedIn(targetFrame)) {
                targetFrame = availableFrames.shift();
            }
        }
    }
    return targetFrame;
}

/**
 * Get DOMRect dimension object of the frame. With IE8- support.
 * @param {object} The frame of which parameters are needed.
 * @return The DOMRect object of the frame.
 */
function getBoundingClientRect(frame) {
    var frameEl = isAccessible(frame.parent) ? frame.frameElement : null,
        documentEl,
        bcr;

    if (frameEl === null) {
        documentEl = frame.document.documentElement;
        bcr = {
            bottom: frame.innerHeight || documentEl.clientHeight,
            height: frame.innerHeight || documentEl.clientHeight,
            left: 0,
            right: frame.innerWidth || documentEl.clientWidth,
            top: 0,
            width: frame.innerWidth || documentEl.clientWidth
        };
    } else {
        bcr = frameEl.getBoundingClientRect();
    }
    return bcr;
}

/**
 * Returns an object that contains sizes calculated (cumulated) from the values included in the input parameter
 * object.
 * @param Array{object} framesInfo The array of the frames in a top-down order. The last frame contains the target
 *                      itself.
 * @return {object} It contains the sizes (absLeft, absTop, left, top, height, width) of the visible area
 *                  of the last frame calculated from the input frames' dimensions.
 */
export function getVisibleClientRectOfFrames(framesInfo) {
    var currentFrame = framesInfo[0],
        currentAbsRight,
        currentAbsBottom,
        calculatedInfo = {
            absLeft: framesInfo[0].left,
            cumulatedAbsLeft: framesInfo[0].left,
            cumulatedAbsRight: framesInfo[0].right,
            absTop: framesInfo[0].top,
            cumulatedAbsTop: framesInfo[0].top,
            cumulatedAbsBottom: framesInfo[0].bottom
        },
        i;

    for (i = 1; i < framesInfo.length; i++) {
        currentFrame = framesInfo[i];

        currentAbsRight = framesInfo[i].right + calculatedInfo.absLeft;
        calculatedInfo.cumulatedAbsRight = Math.min(currentAbsRight, calculatedInfo.cumulatedAbsRight);
        calculatedInfo.absLeft += framesInfo[i].left;
        calculatedInfo.cumulatedAbsLeft = Math.max(calculatedInfo.cumulatedAbsLeft, calculatedInfo.absLeft);

        currentAbsBottom = framesInfo[i].bottom + calculatedInfo.absTop;
        calculatedInfo.cumulatedAbsBottom = Math.min(currentAbsBottom, calculatedInfo.cumulatedAbsBottom);
        calculatedInfo.absTop += framesInfo[i].top;
        calculatedInfo.cumulatedAbsTop = Math.max(calculatedInfo.cumulatedAbsTop, calculatedInfo.absTop);
    }

    return {
        absLeft: calculatedInfo.absLeft,
        absTop: calculatedInfo.absTop,
        left: currentFrame.left,
        top: currentFrame.top,
        height: calculatedInfo.cumulatedAbsBottom - calculatedInfo.cumulatedAbsTop,
        width: calculatedInfo.cumulatedAbsRight - calculatedInfo.cumulatedAbsLeft
    };
}

/**
 * Returns the bounding rectangle data (bottom, height, left, right, top, width) of the visible part of the frame.
 * @param {object} frame The frame of which visible part should be calculated.
 * @return {object} The bounding rectangle of the visible part (DOMRect object + absLeft, absTop values). The
 *         additional values are the left and top distance between the top accessible frame (as the origo of the
 *         coordinate system) and the provided frame.
 */
export function getVisibleClientRect(frame) {
    var ancestorFrames = getAccessibleFramesInHierarchy(frame),
        frameInfo = [],
        visibleFrameInfo,
        i;

    for (i = 0; i < ancestorFrames.length; i++) {
        frameInfo.push(getBoundingClientRect(ancestorFrames[i]));
    }

    visibleFrameInfo = getVisibleClientRectOfFrames(frameInfo);

    return {
        bottom: visibleFrameInfo.height,
        height: visibleFrameInfo.height,
        left: visibleFrameInfo.left,
        right: visibleFrameInfo.width,
        top: visibleFrameInfo.top,
        width: visibleFrameInfo.width,
        absLeft: visibleFrameInfo.absLeft,
        absTop: visibleFrameInfo.absTop
    };
}

/**
     * Returns the inner width of a frame for both desktop and touch device browsers.
     * Note that for touch devices window.innerWidth usually returns a value that is
     * much higher than expected. This function aims to return a more realistic value.
     *
     * @param frame An iframe or the top frame of a page
     */
export function innerWidth(frame) {
    var touchDeviceInnerWidth = 0;
    if (isTouchDevice()) {
        touchDeviceInnerWidth = frame.outerWidth;
    }
    return touchDeviceInnerWidth || frame.innerWidth || frame.document.documentElement.clientWidth;
}

/**
 * Returns the inner height of a frame for both desktop and touch device browsers.
 * Note that for touch devices window.innerHeight usually returns a value that is
 * much higher than expected. This function aims to return a more realistic value.
 *
 * @param frame An iframe or the top frame of a page
 */
export function innerHeight(frame) {
    var touchDeviceInnerHeight = 0;
    if (isTouchDevice()) {
        touchDeviceInnerHeight = frame.outerHeight;
    }
    return touchDeviceInnerHeight || frame.innerHeight || frame.document.documentElement.clientHeight;
}



//////////////////
// WEBPACK FOOTER
// ./shared/modules/frames.js
// module id = 6
// module chunks = main